import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import GlobalAlertProvider from '@user-provider/GlobalAlertProvider';
import SocketProvider from '@user-provider/SocketProvider';

const styles = (theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
});

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        minWidth: 400,
    },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(1),
    },
}))(MuiDialogActions);

/**
 * 글로벌 알림 다이얼로그
 * App.js 최상위에 한 번만 마운트하면, 
 * GlobalAlertProvider.show() 로 어디서든 다이얼로그를 띄울 수 있습니다.
 * 에러 메시지와 함께 최근 시리얼포트 로그도 표시합니다.
 */
export default function GlobalAlertDialog() {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('알림');
    const [message, setMessage] = useState('');
    const [onConfirm, setOnConfirm] = useState(null);
    const [logs, setLogs] = useState([]);
    const logEndRef = useRef(null);

    const handleShow = useCallback((msg, ttl, confirmCallback) => {
        setMessage(msg || '');
        setTitle(ttl || '알림');
        setOnConfirm(() => confirmCallback || null);
        setLogs([...SocketProvider.serialportLogs]);
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        if (onConfirm) {
            onConfirm();
        }
    }, [onConfirm]);

    useEffect(() => {
        GlobalAlertProvider.register(handleShow);
        return () => {
            GlobalAlertProvider.unregister();
        };
    }, [handleShow]);

    // 다이얼로그 열려있을 때 실시간 로그 업데이트
    useEffect(() => {
        if (!open) return;

        const prevHandler = SocketProvider.onSerialportLog;
        SocketProvider.onSerialportLog = (logEntry) => {
            setLogs(prev => {
                const next = [...prev, logEntry];
                return next.length > 200 ? next.slice(-200) : next;
            });
            if (prevHandler) prevHandler(logEntry);
        };
        return () => {
            SocketProvider.onSerialportLog = prevHandler || null;
        };
    }, [open]);

    // 로그 자동 스크롤
    useEffect(() => {
        if (logEndRef.current) {
            logEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="global-alert-dialog-title"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle
                className={'common-top-dialog-title'}
                onClose={handleClose}
            >
                {title}
            </DialogTitle>
            <DialogContent dividers className={'common-top-dialog-content'}>
                {/* 에러 메시지 */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                    <ErrorOutlineIcon style={{ color: '#f44336', fontSize: 28, marginTop: 2 }} />
                    <Typography style={{ whiteSpace: 'pre-line', wordBreak: 'keep-all', fontSize: 15 }}>
                        {message}
                    </Typography>
                </div>

                {/* 시리얼포트 로그 */}
                {logs.length > 0 && (
                    <div>
                        <Typography variant="caption" style={{ color: '#888', marginBottom: 4, display: 'block' }}>
                            시리얼포트 로그 (최근 {logs.length}건)
                        </Typography>
                        <div style={{
                            backgroundColor: '#1e1e1e',
                            borderRadius: 4,
                            padding: 10,
                            maxHeight: 200,
                            overflowY: 'auto',
                        }}>
                            {logs.map((log, i) => (
                                <div key={i} style={{
                                    fontFamily: 'monospace',
                                    fontSize: 11,
                                    color: log.message.startsWith('[오류]') ? '#f44336' : '#d4d4d4',
                                    lineHeight: '18px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                }}>
                                    <span style={{ color: '#6a9955' }}>[{log.time}]</span>{' '}
                                    {log.message}
                                </div>
                            ))}
                            <div ref={logEndRef} />
                        </div>
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant={'contained'}
                    color={'primary'}
                    className={'common-top-dialog-submit'}
                >
                    확인
                </Button>
            </DialogActions>
        </Dialog>
    );
}
